import React, { useState } from 'react';
import { BodyText, Button, FlexRow, ModalWindow, SwitchDialogWindows } from 'ui-toolkit';
import { ILineItem } from './ILineItem';
import { useGetDisbursementsForSettlementItem } from './edit/useGetDisbursementsForSettlementItem';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { eFeePropertyNames } from '../../../types/order/fees/eFeePropertyNames';
import { useProcessDisbursement } from './edit/disbursements/useProcessDisbursement';
import { checkForMutationSuccess } from 'API/checkForMutationSuccess';
import { useDeleteFeeMutation } from 'API/order/fees/FeeApi';
import { convertToError } from 'API/BaseApi';
import { eDisbursementType } from 'types/order/escrow/eDisbursementType';

export interface IConfirmDeleteDialogProps {
  onDone: () => void;
  message: string;
  fee: ILineItem;

  orderId: string;
}

export const ConfirmDeleteDialog: React.FC<IConfirmDeleteDialogProps> = ({ onDone, message, fee, orderId }) => {
  const disbursements = useGetDisbursementsForSettlementItem(fee.disbursementIds).flatMap((t) => t);

  const sentDisbursements = disbursements.filter((disbursement) => {
    switch (disbursement.type) {
      case eDisbursementType.Check:
        return !!disbursement.completedAt || !!disbursement.completedAtDate || !!disbursement.sentAtDate;
      case eDisbursementType.Wire:
        return !!disbursement.enqueuedForDisbursementAt;
      case eDisbursementType.ACH:
        return !!disbursement.sentAt || !!disbursement.completedAt || !!disbursement.disbursedAt;
      case 'Internal Transfer':
        return !!disbursement.enqueuedForDisbursementAt || !!disbursement.completedAt;
    }
    return false;
  });

  const [deleteFeeApi] = useDeleteFeeMutation();

  const processDisbursements = useProcessDisbursement(orderId);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<Error | undefined>(undefined);

  const onDeleteAction = async () => {
    setIsLoading(true);
    for (const disbursement of disbursements) {
      const disbursementToDelete = {
        ...disbursement,
        isDeleting: true,
      };
      const disbursementResult = await processDisbursements(disbursementToDelete);
      if (!checkForMutationSuccess(disbursementResult, `deleting ${disbursementToDelete.type}`)) {
        setError(convertToError(disbursementResult.error));
      }
    }

    const result = await deleteFeeApi({ orderId, feeId: fee.id });
    if (checkForMutationSuccess(result, 'deleting fee')) {
      if (!error) {
        setIsSuccess(true);
        onDone();
      }
    } else {
      setError(convertToError(result.error));
    }
    setIsLoading(false);
  };

  const canDelete = sentDisbursements.length === 0;
  const StyledIcon = canDelete ? (
    <div
      className={'mr-4 flex items-center justify-center rounded-3xl bg-red-100'}
      style={{ width: '40px', height: '40px' }}
    >
      <FontAwesomeIcon className={'text-2xl text-red-600'} icon={faTriangleExclamation} />
    </div>
  ) : (
    <div
      className={'mr-4 flex items-center justify-center rounded-3xl bg-blue-100'}
      style={{ width: '40px', height: '40px' }}
    >
      <FontAwesomeIcon className={'text-2xl text-blue-500'} icon={faInfoCircle} />
    </div>
  );

  return (
    <SwitchDialogWindows
      onCloseClick={onDone}
      onRetryClick={onDeleteAction}
      isError={!!error}
      isLoading={isLoading}
      isCompleted={isSuccess || !!error}
      errorTitle={error && error.message}
      successTitle={'The fee has been deleted'}
    >
      <ModalWindow
        title={
          <span className={'flex flex-row text-4xl'}>
            {StyledIcon}
            <span className={'text-2xl'}>{canDelete ? `Delete Fee?` : `This fee cannot be deleted`}</span>
          </span>
        }
        width={512}
        onClose={onDone}
      >
        <div className={'flex flex-wrap pl-16 text-left align-top'}>
          {!canDelete && (
            <BodyText>
              You cannot delete {fee.stringSettlementStatementPropertyValues[eFeePropertyNames.Name]} because{' '}
              {sentDisbursements.length} disbursement(s) have already been sent.
            </BodyText>
          )}
          {canDelete && (
            <div>
              <BodyText>Are you sure you wish to delete the {message}?</BodyText>
              <BodyText className={'pt-4'}>
                Staged disbursements, split fees, and document associations within this fee line will also be deleted
                permanently.
              </BodyText>
              <BodyText className={'pt-4'}>This action cannot be undone.</BodyText>
            </div>
          )}
        </div>
        <div className={'flex flex-row justify-end pt-4'}>
          <FlexRow justifyContent={'center'}>
            <Button intent={'secondary'} onClick={onDone}>
              {canDelete ? 'Keep Fee' : 'Close'}
            </Button>
            {canDelete && (
              <Button intent={'danger'} onClick={onDeleteAction}>
                Delete Fee
              </Button>
            )}
          </FlexRow>
        </div>
      </ModalWindow>
    </SwitchDialogWindows>
  );
};
